<template>
  <div v-if="isVisibleBalance" class="stripe-balance">
    <a-spin v-if="balanceState === 'loading'" size="small" />

    <template v-else-if="balanceState === 'ready'">
      <span class="title">Stripe Balance:</span>
      <span class="amount">${{ formatBalance }}</span>
    </template>

    <template v-else-if="balanceState === 'not-created'">
      <span class="state-text">{{ setupMessage }}</span>
      <a-button type="link" size="small" @click="openStripeSettings">{{ setupAction }}</a-button>
    </template>

    <template v-else-if="balanceState === 'unavailable'">
      <span class="state-text">Stripe balance is not available for this company.</span>
    </template>

    <template v-else-if="balanceState === 'error'">
      <span class="state-text">Stripe balance is temporarily unavailable.</span>
      <a-button type="link" size="small" @click="retryBalance">Try Again</a-button>
    </template>
  </div>
</template>

<script>
import { Button, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const {
  mapState: stripeState,
  mapGetters: stripeGetters,
  mapActions: stripeActions
} = createNamespacedHelpers('stripe')

export default {
  components: {
    'a-button': Button,
    'a-spin': Spin
  },
  data() {
    return {
      balance: null,
      balanceState: 'idle',
      balanceLoadingKey: '',
      loadedBalanceKey: ''
    }
  },
  computed: {
    ...stripeState({
      stripeProjectId: state => state.projectId,
      accountLoaded: state => state.accountLoaded,
      accountLoading: state => state.accountLoading
    }),
    ...stripeGetters(['accountViewState']),

    currentProjectId() {
      return this.$store.state.applicationState.currentProject?.id
    },
    isVisibleBalance() {
      const permissions = this.$store.state.applicationState.associate?.permissions ?? []
      return (
        Boolean(this.currentProjectId) &&
        permissions.some(item => item.name === 'validate_payments' || item.name === 'process_payments')
      )
    },
    formatBalance() {
      return (this.balance / 100).toFixed(2)
    },
    setupMessage() {
      if (this.accountViewState === 'blocked') return 'Stripe account is restricted.'
      if (this.accountViewState === 'review') return 'Stripe verification is in progress.'
      if (this.accountViewState === 'restricted' || this.accountViewState === 'incomplete') {
        return 'Stripe setup is incomplete.'
      }
      return 'Stripe setup is required.'
    },
    setupAction() {
      if (this.accountViewState === 'new') return 'Create Stripe Account'
      if (this.accountViewState === 'review') return 'Check Stripe Status'
      if (['restricted', 'incomplete'].includes(this.accountViewState)) return 'Continue Stripe Setup'
      if (this.accountViewState === 'blocked') return 'Open Stripe Settings'
      return 'Create Stripe Account'
    },
    balanceKey() {
      return `${this.currentProjectId || ''}`
    }
  },
  watch: {
    currentProjectId: {
      immediate: true,
      handler() {
        this.balanceLoadingKey = ''
        this.loadedBalanceKey = ''
        this.setBalance(null, 'idle')
        this.ensureStripeState()
        this.fetchBalance()
      }
    }
  },
  methods: {
    ...stripeActions(['loadProjectState', 'getProjectAccountBalance']),

    async ensureStripeState() {
      const projectId = this.currentProjectId
      if (!projectId) return
      if (this.stripeProjectId === projectId && (this.accountLoaded || this.accountLoading)) return
      try {
        await this.loadProjectState({ project_id: projectId })
      } catch {
        // Balance errors are rendered inline; account status only refines the setup CTA.
      }
    },
    async fetchBalance() {
      if (!this.currentProjectId) return
      if (this.loadedBalanceKey === this.balanceKey || this.balanceLoadingKey === this.balanceKey) return

      const requestedKey = this.balanceKey
      this.balanceLoadingKey = requestedKey
      this.setBalance(null, 'loading')
      try {
        const balance = await this.getProjectAccountBalance({ project_id: this.currentProjectId })
        if (requestedKey !== this.balanceKey) return
        this.setBalance(balance, 'ready')
        this.loadedBalanceKey = requestedKey
      } catch (error) {
        if (requestedKey !== this.balanceKey) return
        const isAccountMissing = error.status === 409 && error.code === 'stripe_account_not_created'
        const state = isAccountMissing ? 'not-created' : error.status === 404 ? 'unavailable' : 'error'
        this.setBalance(null, state)
      } finally {
        if (this.balanceLoadingKey === requestedKey) this.balanceLoadingKey = ''
      }
    },
    retryBalance() {
      this.loadedBalanceKey = ''
      this.fetchBalance()
    },
    setBalance(balance, state) {
      this.balance = balance
      this.balanceState = state
      this.$emit('updateBalance', balance)
    },
    openStripeSettings() {
      this.$router.push({ name: 'configuration-page.wallets' }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.stripe-balance {
  min-height: 32px;
  border: 1px solid var(--border-color-base);
  border-radius: 4px;
  padding: 0 10px 0 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
}
.title {
  font-weight: 600;
}
.amount {
  white-space: nowrap;
}
.state-text {
  color: var(--text-color-secondary);
}
.stripe-balance .ant-btn {
  padding: 0 4px;
  height: auto;
}
</style>
